import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect } from "react";
import { CentroCustoDropDown } from "../../../../../../common/domain/models/centro_custo_dropdown";
import { DefaultFormAutocomplete } from "../../../../../../common/components/default_form_autocomplete";
import { useForm } from "react-hook-form";
import { ModalLoading } from "../../../../../../common/components/modal_loading";

import FormContainer from "../../../../../../common/components/form_container";
import PrimaryButton from "../../../../../../common/components/primary_button";
import { isNotNullOrUndefined } from "../../../../../../common/utils/utils";
import { LogTipoTabela } from "../../../../../../common/utils/log_tabela_tipo";
import { CentroCustoSecundarioForm, CentroCustoSecundarioSchema } from "../../../../../rh/presentation/schemas/Funcionarios_cadastro/centro_custo_secundario";
import { CentroCustoSecundario } from "../../../../../rh/domain/models/centro_custo_secundario";

/**
 * CadastroCentroCustoSecundarioModal
 */

type Props = {
  open: boolean,
  saving: boolean,
  selectedCentroCustoSecundario: CentroCustoSecundario | null,
  centroCustoLista: CentroCustoDropDown[],
  onSubmit: (form: CentroCustoSecundarioForm) => void,
  onOpenChangeHistory: (id: number, tabelaTipo: LogTipoTabela) => void,
  onClose: () => void,
};

export const CadastroCentroCustoSecundarioModal = ({
  open,
  saving,
  selectedCentroCustoSecundario,
  centroCustoLista,
  onSubmit,
  onOpenChangeHistory,
  onClose,
}: Props) => {
  const hookForm = useForm<CentroCustoSecundarioForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(CentroCustoSecundarioSchema),
  });

  useEffect(() => {
    if (open) {
      if (selectedCentroCustoSecundario) {
        hookForm.setValue('idCentroCusto', selectedCentroCustoSecundario.idCentroCusto);
      } else {
        hookForm.reset();
      }
    }
  }, [
    hookForm,
    open,
    selectedCentroCustoSecundario,
  ]);

  const handleSubmit = useCallback((form: CentroCustoSecundarioForm) => {
    if (!saving) onSubmit(form);
  }, [
    saving,
    onSubmit,
  ]);

  const handleClose = useCallback(() => {
    if (!saving) onClose();
  }, [
    saving,
    onClose,
  ]);

  const handleChangeHistory = useCallback(() => {
    if (selectedCentroCustoSecundario) {
      onOpenChangeHistory(selectedCentroCustoSecundario.idCentroCustoSecundario, LogTipoTabela.FUNCIONARIO_TERC_CENTRO_CUSTO);
    }
  }, [selectedCentroCustoSecundario, onOpenChangeHistory]);

  return (
    <Dialog
      open={open}
      slotProps={{
        paper: {
          component: 'form',
          onSubmit: hookForm.handleSubmit(handleSubmit),
        }
      }}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: "35rem",
          },
        },
      }}>
      <DialogTitle>
        Cadastrar Centro de Custo Secundário
      </DialogTitle>

      <DialogContent>
        <ModalLoading loading={saving}>
          <FormContainer title={"Informações"} arrangement="fullRow" border={false}>
            <>
              <DefaultFormAutocomplete
                name='idCentroCusto'
                label='Centro de Custo'
                required={true}
                readOnly={saving}
                newButtonLabel='Novo centro de custo'
                getOptionLabel={(x) => x.title}
                getOptionKey={(x) => x.id}
                options={centroCustoLista}
                reactHookForm={hookForm}
                onNew={() => { }} />

            </>
          </FormContainer>
        </ModalLoading>
      </DialogContent>

      <DialogActions>
        <div className="w-full h-full items-center justify-between flex p-4 gap-4">
          <div>
            {
              isNotNullOrUndefined(selectedCentroCustoSecundario) &&
              <PrimaryButton
                buttonColor="yellow"
                onClick={handleChangeHistory}>
                <span className="text-xs">
                  Historico de alterações
                </span>
              </PrimaryButton>
            }
          </div>

          <div className="flex flex-row gap-2">
            <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
              <h1
                className="text-sm text-black dark:text-white"
                onClick={handleClose}>
                Cancelar
              </h1>
            </div>

            <div className="w-[120px]">
              {saving ? (
                <PrimaryButton
                  buttonColor="primary-green">
                  Salvando...
                </PrimaryButton>
              ) : (
                <PrimaryButton
                  type="submit"
                  buttonColor="primary-green">
                  Salvar
                </PrimaryButton>
              )}
            </div>
          </div>
        </div>
      </DialogActions>
    </Dialog>
  );
}